// React import
import { useRef, useEffect, useState } from 'react';

// Generic imports
import _ from 'lodash';

// Mui imports
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Modal from '@mui/material/Modal';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemButton from '@mui/material/ListItemButton';
import Checkbox from '@mui/material/Checkbox';
import Alert from '@mui/material/Alert';

// Local imports
import LegEditor from '../../LegEditor.js'

function OpportunityTitleEditModal(props) {
    const { modal, setModal, offers, setOffers, seaPorts, unlocode, transportationMethod } = props;
    const inputRef = useRef();

    const [ days, setDays ] = useState(0);
    const [ hours, setHours ] = useState(0);
    const [ minutes, setMinutes ] = useState(0);
    const [ routeErrors, setRouteErrors ] = useState([]);

    useEffect(() => {
        if (modal.inputType !== 'transit-time') return;
        const v = parseFloat(modal.value || 0);
        const days = parseInt(parseInt(v) / 24);
        const hours = parseInt(v) % 24;
        const minutes = parseInt((v - days * 24 - hours) * 60);
        setDays(days)
        setHours(hours);
        setMinutes(minutes);
    }, [modal.value, modal.open, modal.inputType])

    useEffect(() => {
        setTimeout(() => {
            if (modal.open && inputRef.current) {
                inputRef.current.focus();
                inputRef.current.select();
            }
        }, 100);
    }, [modal.open]);

    function handleToggle(id) {
        setModal({ 
            ...modal, 
            value: _.xor(modal.value, [id])
        });
    }

    // A validity range can't end before it starts. Dates are yyyy-mm-dd, so they compare as text.
    const isValidity = modal.inputType === 'validity' || modal.inputType === 'validity-range';
    const validityError = isValidity && modal.value?.validFrom && modal.value?.validUntil && modal.value.validUntil < modal.value.validFrom
        ? 'The end date is before the start date.'
        : null;

    function onOk() {
        if (validityError) return;
        const value = modal.inputType === 'transit-time' ? parseInt(days || 0) * 24 + parseInt(hours || 0) + (minutes || 0) / 60 : modal.value;
        
        // Validate routes: check if any route has missing fields
        if (modal.inputType === 'routes') {
            const routes = value || [];
            const errors = [];
            
            routes.forEach((route, index) => {
                const hasSource = route.source && route.source.text && route.source.text.trim() !== '';
                const hasDestination = route.destination && route.destination.text && route.destination.text.trim() !== '';
                
                if (!hasSource && hasDestination) {
                    errors.push(`where:${index}:address-source-required`);
                }
                if (hasSource && !hasDestination) {
                    errors.push(`where:${index}:address-destination-required`);
                }
            });
            
            if (errors.length > 0) {
                setRouteErrors(errors);
                return;
            }
            
            setRouteErrors([]);
        }
        
        if (!modal.path) {
            modal.onOk(value);
            setModal({ ...modal, open: false });
            return;
        }
        const newOffers = _.cloneDeep(offers);
        if (modal.inputType === 'validity-range') {
            // Both dates sit on the offer or section at `path`; a cleared date is removed.
            for (const key of ['validFrom', 'validUntil']) {
                if (value?.[key]) _.set(newOffers, `${modal.path}.${key}`, value[key]);
                else _.unset(newOffers, `${modal.path}.${key}`);
            }
            setOffers(newOffers);
            setModal({ ...modal, open: false });
            return;
        }
        _.set(newOffers, modal.path, modal.inputType === 'routes' && _.isEmpty(value) ? null : value);
        setOffers(newOffers);
        setModal({ ...modal, open: false });
    }

    return (
        <Modal 
            open={modal.open} 
            sx={{ overflow: 'visible', zIndex: 100000000000 }} 
            onClose={() => setModal({ ...modal, open: false })} 
            disableAutoFocus
        >
            <Card sx={{ ...MODAL_STYLE, width: '560px'}}>
                <CardContent>
                    <Typography gutterBottom variant='h5' component='div'>
                        {modal.title}
                    </Typography>
                    {modal.description && (
                        <Box sx={{ margin: '12px 0px' }}>
                            {modal.description}
                        </Box>
                    )}
                    {modal.options && (
                        <Select
                            value={modal.value}
                            onChange={e => setModal({ ...modal, value: e.target.value })}
                            fullWidth
                            sx={{ marginTop: '12px' }}
                        >
                            {modal.options.map(option => (
                                <MenuItem value={option.value} key={option.value}>{option.label}</MenuItem>
                            ))}
                        </Select>
                    )} 
                    {!modal.options && !modal.checkboxes && modal.inputType !== 'routes' && modal.inputType !== 'validity' && modal.inputType !== 'validity-range' && modal.inputType !== 'transit-time' && (
                        <TextField
                            id='modalField'
                            label={modal.label}
                            value={modal.value}
                            type={modal.inputType}
                            onChange={e => setModal({ ...modal, value: e.target.value })}
                            fullWidth
                            sx={{ marginTop: '12px' }}
                            inputRef={inputRef}
                            onKeyDown={(e) => {
                                if (e.keyCode === 13) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onOk();
                                }
                            }}
                        />
                    )}
                    {modal.inputType === 'transit-time' && (
                        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                            <TextField
                                id='modalField'
                                label={'Days'}
                                value={days}
                                onChange={e => setDays(e.target.value)}
                                sx={{ marginTop: '12px' }}
                            />
                            <TextField
                                id='modalField'
                                label={'Hours'}
                                value={hours}
                                onChange={e => setHours(e.target.value)}
                                sx={{ marginTop: '12px' }}
                            />
                            <TextField
                                id='modalField'
                                label={'Minutes'}
                                value={minutes}
                                onChange={e => setMinutes(e.target.value)}
                                sx={{ marginTop: '12px' }}
                            />
                        </Box>
                    )}
                    {(modal.inputType === 'validity' || modal.inputType === 'validity-range') && (
                        <Box>
                            <TextField
                                id='modalField'
                                label={'Valid From'}
                                value={modal.value.validFrom}
                                type={'date'}
                                onChange={e => setModal({ ...modal, value: {...modal.value, validFrom: e.target.value}})}
                                fullWidth
                                sx={{ marginTop: '12px' }}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                            <TextField
                                id='modalField'
                                label={'Valid Until'}
                                value={modal.value.validUntil}
                                type={'date'}
                                inputProps={{ min: modal.value.validFrom || undefined }}
                                error={!!validityError}
                                helperText={validityError}
                                onChange={e => setModal({ ...modal, value: {...modal.value, validUntil: e.target.value} })}
                                fullWidth
                                sx={{ marginTop: '12px' }}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                            {/* Empties both boxes; OK then removes the validity from the quote. */}
                            {modal.inputType === 'validity-range' && (
                                <Button
                                    size='small'
                                    sx={{ marginTop: '8px', textTransform: 'none' }}
                                    disabled={!modal.value?.validFrom && !modal.value?.validUntil}
                                    onClick={() => setModal({ ...modal, value: { validFrom: '', validUntil: '' } })}
                                >
                                    Clear dates
                                </Button>
                            )}
                        </Box>
                    )}
                    {modal.checkboxes && (
                        <List>
                            {modal.checkboxes.items.map(({ id, title }) => (
                                <ListItem key={id} disablePadding>
                                    <ListItemButton onClick={() => handleToggle(id)} dense>
                                        <ListItemIcon>
                                            <Checkbox
                                                edge='start'
                                                checked={modal.value.includes(id)}
                                                tabIndex={-1}
                                                disableRipple
                                            />
                                        </ListItemIcon>
                                        <ListItemText primary={title} />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    )}
                    {!modal.options && !modal.checkboxes && modal.inputType === 'routes' && (
                        <>
                            <LegEditor 
                                transportationMethod={transportationMethod}
                                routes={_.isEmpty(modal.value) ? [] : modal.value}
                                setRoutes={e => {
                                    setModal({ ...modal, value: e });
                                    setRouteErrors([]); // Clear errors when routes change
                                }}
                                setTransportationMethod={null}
                                autoPopulateSource={true}
                                seaPorts={seaPorts}
                                unlocode={unlocode}
                                canBeEmpty={true}
                                errors={routeErrors}
                            />
                            {routeErrors.some(e => e.includes('address-source-required')) && (
                                <Alert severity='error' sx={{ marginTop: '12px' }}>
                                    Route as to have an origin
                                </Alert>
                            )}
                            {routeErrors.some(e => e.includes('address-destination-required')) && (
                                <Alert severity='error' sx={{ marginTop: '12px' }}>
                                    Route as to have a destination
                                </Alert>
                            )}
                        </>
                    )}
                    {!_.isEmpty(modal.suggestions) && (
                        <Typography variant='caption' component={Box} sx={{ marginTop: '18px' }}>
                            <Box>Suggestions:</Box>
                            {modal.suggestions.map(suggest => (
                                <Button variant='outlined' sx={{ margin: '3px', textTransform: 'none', fontSize: '11px !important' }} onClick={() => setModal({ ...modal, value: suggest })}>
                                    {suggest}
                                </Button>
                            ))}
                        </Typography>
                    )}
                </CardContent>
                <CardActions style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Button size='small' color='secondary' onClick={() => { setModal({ ...modal, open: false }) }}>
                        Cancel
                    </Button>
                    <Button
                        size='small' 
                        color='primary' 
                        variant='contained' 
                        disabled={!!validityError}
                        onClick={() => {
                            onOk()
                        }}
                    >
                        OK
                    </Button>
                </CardActions>
            </Card>
        </Modal>
    );
}

export default OpportunityTitleEditModal;

const MODAL_STYLE = {
    overflow: 'visible',
    position: 'absolute',
    top: '50px',
    left: 'calc(50% - 280px)',
    width: 400,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    pt: 2,
    px: 4,
    pb: 3,
};

